/**
 * EventHoverCard
 *
 * Envolve o elemento de um evento (chip/bloco) e mostra o EventPreviewCard num
 * tooltip de hover. Resolve responsável (userId → nome) e contato vinculado
 * (contactId → nome/telefone) via dropdowns cacheadas (react-query dedupe por
 * queryKey — um único fetch compartilhado entre todos os eventos).
 *
 * Uso (em qualquer view — mês/semana/dia/agenda):
 *   <EventHoverCard event={event}>{elementoDoEvento}</EventHoverCard>
 */

import React from 'react';
import { CalendarEventResponse } from '@troia-v3/types';
import { useUsersDropdown } from '@/modules/users/hooks/useUsers';
import { useContactsDropdown } from '@/modules/contacts/hooks/useContactsDropdown';
import { HoverCard } from './HoverCard';
import { EventPreviewCard } from './EventPreviewCard';

interface EventHoverCardProps {
  event: CalendarEventResponse;
  children: React.ReactElement;
}

export const EventHoverCard: React.FC<EventHoverCardProps> = ({ event, children }) => {
  const { data: usersData } = useUsersDropdown({ status: 'active' });
  const { data: contactsData } = useContactsDropdown();

  const ownerName = event.userId ? usersData?.find(u => u.id === event.userId)?.name : undefined;
  const contact = event.contactId ? contactsData?.find(c => c.id === event.contactId) : undefined;

  return (
    <HoverCard
      card={
        <EventPreviewCard
          event={event}
          ownerName={ownerName}
          contactName={contact?.name}
          contactPhone={contact?.phone}
        />
      }
    >
      {children}
    </HoverCard>
  );
};

EventHoverCard.displayName = 'EventHoverCard';
